import React, {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from 'react'
import { switchRegionCognitoConfig } from 'utils'

export interface Config {
  authType: string
  authByButtonAction: boolean
  amplifyConfig: {
    Auth: {
      identityPoolId: string
      region: string
      userPoolId: string
      userPoolWebClientId: string
      mandatorySignIn: boolean
      oauth: {
        domain: string
        scope: string[]
        redirectSignIn: string
        redirectSignOut: string
        responseType: string
      }
      federationTarget: string
    }
  }
  idpProvider: string
  megaMenuUrl: string
  showingChoiceServicesUrl: string
  showingChoiceManagementUrl: string
  sentrikeyAccountSetupUrl: string
  FLAGSMITH_ENV_APIKEY: string
}

interface ConfigContextProps {
  config: Config | null
  configError: string | null
  configLoading: boolean
  setConfig: (config: Config) => void
}

const ConfigContext = createContext<ConfigContextProps>({
  config: null,
  configError: null,
  configLoading: true,
  setConfig: () => {},
})

interface ConfigProviderProps {
  children: ReactNode
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<Config | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)
  const [configLoading, setConfigLoading] = useState(true)

  useEffect(() => {
    const fetchConfig = async () => {
      setConfigLoading(true)
      setConfigError(null)
      try {
        const response = await fetch('./config.json')
        if (!response.ok) {
          throw new Error(
            `Failed to load config.json: ${response.status} ${response.statusText}`,
          )
        }
        const loadedConfig: Config = await response.json()
        await switchRegionCognitoConfig(loadedConfig, setConfig)
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred'
        console.error('Error loading configuration:', errorMessage)
        setConfigError('Error loading configuration: ' + errorMessage)
        setConfig(null)
      } finally {
        setConfigLoading(false)
      }
    }

    fetchConfig()
  }, [])

  const contextValue = { config, configError, configLoading, setConfig }

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  )
}

export const useConfig = (): ConfigContextProps => {
  const context = useContext(ConfigContext)
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider')
  }
  return context
}
